import { useCallback, useEffect, useRef, useState } from 'react'
import { AlphabetAudioPlayer } from './alphabet-audio'
import type { AlphabetAudioState } from './alphabet-audio'
import { learningAudioCache } from '@shared/media/audio/learning-audio-cache'
import type { LearningAudioLease } from '@shared/media/audio/learning-audio-cache'
import { observeLearningAudio } from '@shared/media/audio/observe-learning-audio'

export function useAlphabetAudioPlayer(sources: readonly string[]) {
  const playerRef = useRef<AlphabetAudioPlayer | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [state, setState] = useState<AlphabetAudioState>({ key: null, playbackState: 'idle', failureReason: null, engine: 'WEB_AUDIO' })
  const sourceSignature = JSON.stringify(sources)
  useEffect(() => {
    const player = new AlphabetAudioPlayer({
      loadEncoded: (source, signal) => learningAudioCache.read(source, signal),
      resolveNativeSource: source => learningAudioCache.prepared(source) ?? source,
    })
    playerRef.current = player
    const unsubscribe = player.subscribe(setState)
    let leases: LearningAudioLease[] = []
    const prepare = () => {
      const sources = JSON.parse(sourceSignature) as string[]
      leases = sources.map(source => learningAudioCache.acquire(source))
      leases.forEach(lease => { void lease.ready.catch(() => undefined) })
      void player.prepare(sources)
    }
    const stopObserving = containerRef.current ? observeLearningAudio(containerRef.current, prepare) : undefined
    return () => {
      playerRef.current = null
      stopObserving?.()
      unsubscribe()
      player.dispose()
      leases.forEach(lease => lease.release())
    }
  }, [sourceSignature])
  const toggle = useCallback((key: string, source: string) => playerRef.current?.toggle(key, source), [])
  return { state, toggle, containerRef }
}
